import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";

// ── Constants ─────────────────────────────────────────────────────────────────
const GOLD = "#9c6a36";
const GOLD18 = "#9c6a3618";

const cardSx = {
  borderRadius: 2,
  border: "0.1px solid",
  borderColor: "customDivider",
  backgroundColor: "offWhite",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  height: "100%",
  boxSizing: "border-box",
};

const STATUS_COLORS = {
  confirmed: "#378ADD",
  completed: "#1D9E75",
  cancelled: "#E24B4A",
  no_show: "#EF9F27",
  pending: "#7F77DD",
};

const PERIODS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "60 days", days: 60 },
  { label: "90 days", days: 90 },
];

const DOW_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const labelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  color: GOLD,
  display: "block",
  mb: 2,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });

const calcDelta = (curr, prev) => {
  if (prev === 0 && curr === 0) return null;
  if (prev === 0) return null;
  const d = ((curr - prev) / Math.abs(prev)) * 100;
  return `${d >= 0 ? "+" : ""}${Math.round(d)}%`;
};

// ── Sort toggle ───────────────────────────────────────────────────────────────
const SortToggle = ({ value, onChange }) => (
  <Box sx={{ display: "flex", gap: 0.5 }}>
    {[
      { key: "count", label: "Appts" },
      { key: "revenue", label: "Revenue" },
    ].map(({ key, label }) => (
      <Box
        key={key}
        onClick={() => onChange(key)}
        sx={{
          px: 1.25,
          py: 0.25,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          border: "1px solid",
          borderColor: value === key ? "#1a1a1a" : "customDivider",
          backgroundColor: value === key ? "#1a1a1a" : "transparent",
          color: value === key ? "white" : "text.secondary",
          transition: "all 0.15s",
          userSelect: "none",
        }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, deltaVal, positiveIsGood = true }) => {
  const isPositive = deltaVal?.startsWith("+");
  const deltaColor = isPositive
    ? positiveIsGood
      ? "#1D9E75"
      : "#E24B4A"
    : positiveIsGood
      ? "#E24B4A"
      : "#1D9E75";

  return (
    <Box sx={{ ...cardSx, p: 2.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: GOLD,
          mb: 1.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h3"
        sx={{ fontWeight: 700, letterSpacing: -0.5, mb: 0.25 }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block" }}
        >
          {sub}
        </Typography>
      )}
      {deltaVal && (
        <Typography
          variant="caption"
          sx={{ color: deltaColor, fontWeight: 600, display: "block", mt: 0.5 }}
        >
          {deltaVal} vs prev period
        </Typography>
      )}
    </Box>
  );
};

// ── Revenue tooltip ───────────────────────────────────────────────────────────
const RevenueTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        backgroundColor: "offWhite",
        border: "1px solid #f0f0f0",
        borderRadius: 1.5,
        p: 1.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {fmt(payload[0].value)}
      </Typography>
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const BarberAnalytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();
  const { user } = useAuth();

  const [activePeriod, setActivePeriod] = useState(30);
  const [isCustom, setIsCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState(
    dayjs().subtract(30, "day").format("YYYY-MM-DD"),
  );
  const [customTo, setCustomTo] = useState(dayjs().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(true);
  const [curr, setCurr] = useState([]);
  const [prev, setPrev] = useState([]);
  const [serviceSort, setServiceSort] = useState("count");

  const { dateFrom, dateTo } = useMemo(() => {
    if (isCustom) return { dateFrom: customFrom, dateTo: customTo };
    return {
      dateFrom: dayjs()
        .subtract(activePeriod - 1, "day")
        .format("YYYY-MM-DD"),
      dateTo: dayjs().format("YYYY-MM-DD"),
    };
  }, [isCustom, activePeriod, customFrom, customTo]);

  const days = Math.max(dayjs(dateTo).diff(dayjs(dateFrom), "day") + 1, 1);
  const prevDateTo = dayjs(dateFrom).subtract(1, "day").format("YYYY-MM-DD");
  const prevDateFrom = dayjs(dateFrom)
    .subtract(days, "day")
    .format("YYYY-MM-DD");

  const load = useCallback(async () => {
    if (!user?.user_id) return;
    setLoading(true);
    const id = user.user_id;
    try {
      const [currRes, prevRes] = await Promise.all([
        api(
          `/tenants/appointments.php?date_from=${dateFrom}&date_to=${dateTo}&barber_ids=${id}&refund_by_update=1`,
          { method: "GET" },
        ),
        api(
          `/tenants/appointments.php?date_from=${prevDateFrom}&date_to=${prevDateTo}&barber_ids=${id}`,
          { method: "GET" },
        ),
      ]);
      setCurr(currRes.data.bookings || []);
      setPrev(prevRes.data.bookings || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [user?.user_id, dateFrom, dateTo, prevDateFrom, prevDateTo]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Derived metrics ───────────────────────────────────────────────────────
  // curr is fetched with refund_by_update=1, so it can include bookings refunded
  // in this period whose appointment falls outside it. Restrict metrics to
  // in-period appointments. (prev isn't fetched with the flag, but filter for parity.)
  const inRange = (b, from, to) => {
    const d = (b.starts_at_local || b.starts_at || "").slice(0, 10);
    return d >= from && d <= to;
  };
  const periodCurr = curr.filter((b) => inRange(b, dateFrom, dateTo));
  const periodPrev = prev.filter((b) => inRange(b, prevDateFrom, prevDateTo));
  const isPaidTxn = (b) =>
    Number(b.amount_paid) > 0 &&
    !(b.status === "cancelled" && b.payment_status === "refunded");

  const completed = periodCurr.filter((b) => b.status === "completed");
  const cancelled = periodCurr.filter((b) => b.status === "cancelled");
  const noShows = periodCurr.filter((b) => b.status === "no_show");
  const confirmed = periodCurr.filter((b) => b.status === "confirmed");
  const pending = periodCurr.filter((b) => b.status === "pending");
  const nonCancelled = periodCurr.filter((b) => b.status !== "cancelled");

  // Revenue = money actually collected (incl. no-show fees and forfeited
  // cancellations), matching the admin dashboard — not completed-only.
  const paidTxns = periodCurr.filter(isPaidTxn);
  const revenue = paidTxns.reduce((s, b) => s + Number(b.amount_paid || 0), 0);
  const avgTicket = paidTxns.length ? revenue / paidTxns.length : 0;
  const avgDailyRevenue = revenue / days;

  const complDenom = completed.length + noShows.length + cancelled.length;
  const complRate = complDenom > 0 ? (completed.length / complDenom) * 100 : 0;
  const cancRate = periodCurr.length
    ? (cancelled.length / periodCurr.length) * 100
    : 0;
  const nsRate = periodCurr.length
    ? (noShows.length / periodCurr.length) * 100
    : 0;

  const prevCompleted = periodPrev.filter((b) => b.status === "completed");
  const prevCancelled = periodPrev.filter((b) => b.status === "cancelled");
  const prevNoShows = periodPrev.filter((b) => b.status === "no_show");
  const prevPaidTxns = periodPrev.filter(isPaidTxn);
  const prevRevenue = prevPaidTxns.reduce(
    (s, b) => s + Number(b.amount_paid || 0),
    0,
  );
  const prevAvgTicket = prevPaidTxns.length
    ? prevRevenue / prevPaidTxns.length
    : 0;
  const prevComplDenom =
    prevCompleted.length + prevNoShows.length + prevCancelled.length;
  const prevComplRate =
    prevComplDenom > 0 ? (prevCompleted.length / prevComplDenom) * 100 : 0;

  const deltaAppts = calcDelta(
    nonCancelled.length,
    periodPrev.filter((b) => b.status !== "cancelled").length,
  );
  const deltaRevenue = calcDelta(revenue, prevRevenue);
  const deltaTicket = calcDelta(avgTicket, prevAvgTicket);
  const deltaCompl = calcDelta(complRate, prevComplRate);
  const deltaCanc = calcDelta(
    cancRate,
    periodPrev.length ? (prevCancelled.length / periodPrev.length) * 100 : 0,
  );
  const deltaNS = calcDelta(
    nsRate,
    periodPrev.length ? (prevNoShows.length / periodPrev.length) * 100 : 0,
  );

  // Daily revenue for chart
  const dailyRevenue = useMemo(() => {
    const map = {};
    curr
      .filter(
        (b) =>
          Number(b.amount_paid) > 0 &&
          !(b.status === "cancelled" && b.payment_status === "refunded"),
      )
      .forEach((b) => {
        const d = (b.starts_at_local || "").slice(0, 10);
        map[d] = (map[d] || 0) + Number(b.amount_paid || 0);
      });
    const result = [];
    let cur = dayjs(dateFrom);
    const end = dayjs(dateTo);
    while (cur.isSame(end, "day") || cur.isBefore(end, "day")) {
      const key = cur.format("YYYY-MM-DD");
      result.push({ date: cur.format("MMM D"), revenue: map[key] || 0 });
      cur = cur.add(1, "day");
    }
    if (result.length > 60) {
      const weekly = [];
      for (let i = 0; i < result.length; i += 7) {
        const slice = result.slice(i, i + 7);
        weekly.push({
          date: slice[0].date,
          revenue: slice.reduce((s, d) => s + d.revenue, 0),
        });
      }
      return weekly;
    }
    return result;
  }, [curr, dateFrom, dateTo]);

  // Status donut data
  const statusData = [
    {
      name: "Completed",
      value: completed.length,
      color: STATUS_COLORS.completed,
    },
    {
      name: "Confirmed",
      value: confirmed.length,
      color: STATUS_COLORS.confirmed,
    },
    { name: "Pending", value: pending.length, color: STATUS_COLORS.pending },
    {
      name: "Cancelled",
      value: cancelled.length,
      color: STATUS_COLORS.cancelled,
    },
    { name: "No-show", value: noShows.length, color: STATUS_COLORS.no_show },
  ].filter((d) => d.value > 0);

  // Top services
  const serviceStats = useMemo(() => {
    const map = {};
    curr
      .filter((b) => b.status !== "cancelled")
      .forEach((b) => {
        const key = b.service?.name || "Unknown";
        if (!map[key]) map[key] = { name: key, bookings: 0, revenue: 0 };
        map[key].bookings++;
        map[key].revenue += parseFloat(b.price_charged || 0);
      });
    return Object.values(map).sort((a, b) =>
      serviceSort === "revenue"
        ? b.revenue - a.revenue
        : b.bookings - a.bookings,
    );
  }, [curr, serviceSort]);

  const maxServiceCount = Math.max(...serviceStats.map((s) => s.bookings), 1);
  const maxServiceRevenue = Math.max(...serviceStats.map((s) => s.revenue), 1);

  // Busiest days
  const dowStats = useMemo(() => {
    const map = Array(7)
      .fill(0)
      .map((_, i) => ({ day: DOW_NAMES[i], appts: 0 }));
    curr
      .filter((b) => b.status !== "cancelled")
      .forEach((b) => {
        const d = dayjs(b.starts_at_local || b.starts_at).day();
        map[d].appts++;
      });
    return map;
  }, [curr]);
  const maxDow = Math.max(...dowStats.map((d) => d.appts), 1);

  // Busiest hours
  const hourStats = useMemo(() => {
    const map = {};
    curr
      .filter((b) => b.status !== "cancelled")
      .forEach((b) => {
        const h = dayjs(b.starts_at_local || b.starts_at).hour();
        map[h] = (map[h] || 0) + 1;
      });
    return Array.from({ length: 14 }, (_, i) => i + 7).map((h) => ({
      hour: h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`,
      appts: map[h] || 0,
    }));
  }, [curr]);

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, py: 5 }}>
      {/* ── Header + period selector ─────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant={isMobile ? "h4" : "h3"} sx={{ mb: 0.25 }}>
            Analytics
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Your personal performance.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          {PERIODS.map(({ label, days: d }) => {
            const active = !isCustom && activePeriod === d;
            return (
              <Box
                key={d}
                component="button"
                onTouchEnd={(e) => { e.preventDefault(); setActivePeriod(d); setIsCustom(false); }}
                onClick={() => { setActivePeriod(d); setIsCustom(false); }}
                sx={{
                  backgroundColor: active ? "#1a1a1a" : "white",
                  color: active ? "white" : "#1a1a1a",
                  border: "1px solid",
                  borderColor: active ? "#1a1a1a" : "customDivider",
                  fontWeight: 500,
                  fontSize: 13,
                  height: 32,
                  minWidth: 0,
                  px: 1.5,
                  borderRadius: "16px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "@media (hover: hover)": {
                    "&:hover": { backgroundColor: active ? "#000" : "#f5f5f5" },
                  },
                }}
              >
                {label}
              </Box>
            );
          })}
          <Box
            component="button"
            onTouchEnd={(e) => { e.preventDefault(); setIsCustom(true); }}
            onClick={() => setIsCustom(true)}
            sx={{
              backgroundColor: isCustom ? "#1a1a1a" : "white",
              color: isCustom ? "white" : "#1a1a1a",
              border: "1px solid",
              borderColor: isCustom ? "#1a1a1a" : "customDivider",
              fontWeight: 500,
              fontSize: 13,
              height: 32,
              minWidth: 0,
              px: 1.5,
              borderRadius: "16px",
              cursor: "pointer",
              fontFamily: "inherit",
              "@media (hover: hover)": {
                "&:hover": { backgroundColor: isCustom ? "#000" : "#f5f5f5" },
              },
            }}
          >
            Custom
          </Box>
          {isCustom && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                type="date"
                size="small"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    fontSize: 13,
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                to
              </Typography>
              <TextField
                type="date"
                size="small"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                slotProps={{ htmlInput: { min: customFrom } }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    fontSize: 13,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 8 }}>
          <CircularProgress size={18} sx={{ color: "text.secondary" }} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading analytics...
          </Typography>
        </Box>
      ) : (
        <>
          {/* ── KPI cards ──────────────────────────────────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
              gap: 2,
              mb: 4,
            }}
          >
            <StatCard
              label="Appointments"
              value={nonCancelled.length}
              sub={`over ${days} days`}
              deltaVal={deltaAppts}
            />
            <StatCard
              label="Revenue"
              value={fmt(revenue)}
              sub={`${fmt(avgDailyRevenue)}/day avg`}
              deltaVal={deltaRevenue}
            />
            <StatCard
              label="Avg Ticket"
              value={fmt(avgTicket)}
              sub="per completed appt"
              deltaVal={deltaTicket}
            />
            <StatCard
              label="Completion Rate"
              value={`${Math.round(complRate)}%`}
              sub={`${completed.length} completed`}
              deltaVal={deltaCompl}
            />
            <StatCard
              label="Cancellation Rate"
              value={`${Math.round(cancRate)}%`}
              sub={`${cancelled.length} cancelled`}
              deltaVal={deltaCanc}
              positiveIsGood={false}
            />
            <StatCard
              label="No-show Rate"
              value={`${Math.round(nsRate)}%`}
              sub={`${noShows.length} no-shows`}
              deltaVal={deltaNS}
              positiveIsGood={false}
            />
          </Box>

          {/* ── Revenue chart + Status pie ──────────────────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
              gap: 3,
              mb: 4,
            }}
          >
            <Box sx={{ ...cardSx, p: 3 }}>
              <Typography sx={labelSx}>Revenue Over Time</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={dailyRevenue}
                  margin={{ top: 0, right: 0, left: -10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#9b9b9b" }}
                    tickLine={false}
                    axisLine={false}
                    interval={
                      dailyRevenue.length > 30
                        ? Math.floor(dailyRevenue.length / 10)
                        : "preserveStartEnd"
                    }
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#9b9b9b" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) =>
                      v === 0 ? "$0" : `$${(v / 1000).toFixed(0)}k`
                    }
                  />
                  <Tooltip
                    content={<RevenueTooltip />}
                    cursor={{ fill: GOLD18 }}
                  />
                  <Bar dataKey="revenue" fill={GOLD} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ ...cardSx, p: 3 }}>
              <Typography sx={labelSx}>Appointment Status</Typography>
              {statusData.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No data for this period.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {statusData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, name) => [`${v} appts`, name]} />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      formatter={(v) => (
                        <span style={{ fontSize: 12, color: "#4b4b4b" }}>
                          {v}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>

          {/* ── Top services ────────────────────────────────────────── */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ ...cardSx, overflow: "hidden" }}>
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                  Top Services
                </Typography>
                <SortToggle value={serviceSort} onChange={setServiceSort} />
              </Box>
              <Box sx={{ px: 3, py: 2 }}>
                {serviceStats.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No appointments yet.
                  </Typography>
                ) : (
                  serviceStats.map((s, i) => (
                    <Box
                      key={s.name}
                      sx={{ mb: i < serviceStats.length - 1 ? 2.5 : 0 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          noWrap
                        >
                          {s.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", flexShrink: 0, ml: 1 }}
                        >
                          {serviceSort === "revenue"
                            ? fmt(s.revenue)
                            : `${s.bookings} appt${s.bookings !== 1 ? "s" : ""}`}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: "#f0efec",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${Math.round(((serviceSort === "revenue" ? s.revenue : s.bookings) / (serviceSort === "revenue" ? maxServiceRevenue : maxServiceCount)) * 100)}%`,
                            backgroundColor: GOLD,
                            borderRadius: 3,
                            transition: "width 0.6s ease",
                          }}
                        />
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>

          {/* ── Busiest days + Busiest hours ─────────────────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: 3,
            }}
          >
            <Box sx={{ ...cardSx, p: 3 }}>
              <Typography sx={labelSx}>Busiest Days of the Week</Typography>
              <Box sx={{ overflowX: "auto", mx: -1 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: 1.5,
                    minWidth: 280,
                    px: 1,
                  }}
                >
                  {dowStats.map((d) => (
                    <Box key={d.day} sx={{ textAlign: "center" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          fontSize: 10,
                          display: "block",
                          mb: 1,
                        }}
                      >
                        {d.day}
                      </Typography>
                      <Box
                        sx={{
                          height: 80,
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          mb: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: "60%",
                            height: `${Math.max((d.appts / maxDow) * 100, 4)}%`,
                            backgroundColor:
                              d.appts === maxDow ? GOLD : "#f0efec",
                            borderRadius: "3px 3px 0 0",
                            transition: "height 0.4s ease",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: d.appts === maxDow ? GOLD : "text.primary",
                        }}
                      >
                        {d.appts}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ ...cardSx, p: 3 }}>
              <Typography sx={labelSx}>Busiest Hours of the Day</Typography>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart
                  data={hourStats}
                  margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
                >
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 10, fill: "#9b9b9b" }}
                    tickLine={false}
                    axisLine={false}
                    interval={1}
                  />
                  <YAxis hide />
                  <Tooltip
                    formatter={(v) => [`${v} appts`, "Appointments"]}
                    cursor={{ fill: GOLD18 }}
                  />
                  <Bar dataKey="appts" fill={GOLD} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BarberAnalytics;
